import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getReadiness, saveReadiness } from "@/lib/api";
import { ShieldCheck, CheckCircle2, Clock, Circle, MinusCircle, Building2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  have: { label: "Have it", icon: CheckCircle2, cls: "bg-emerald-600 text-white border-emerald-600", dot: "text-emerald-600" },
  in_progress: { label: "In progress", icon: Clock, cls: "bg-amber-500 text-white border-amber-500", dot: "text-amber-500" },
  not_yet: { label: "Not yet", icon: Circle, cls: "bg-zinc-900 text-white border-zinc-900", dot: "text-zinc-400" },
  na: { label: "N/A", icon: MinusCircle, cls: "bg-zinc-200 text-zinc-600 border-zinc-300", dot: "text-zinc-300" },
};
const ORDER = ["have", "in_progress", "not_yet", "na"];

export default function Readiness() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["readiness"], queryFn: getReadiness });
  const [notes, setNotes] = useState({});

  useEffect(() => {
    if (data) {
      const n = {};
      data.groups.forEach((g) => g.items.forEach((i) => { n[i.id] = i.notes || ""; }));
      setNotes(n);
    }
  }, [data]);

  const setStatus = async (id, status) => {
    try {
      await saveReadiness(id, { status });
      qc.invalidateQueries({ queryKey: ["readiness"] });
    } catch (e) {
      toast.error("Could not save");
    }
  };
  const saveNote = async (id) => {
    try {
      await saveReadiness(id, { notes: notes[id] || "" });
      toast.success("Note saved");
    } catch (e) {
      toast.error("Could not save note");
    }
  };

  const percent = data?.percent ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#2F6DA3] mb-1">Domestic → Commercial · Main Contractor</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter">Commercial Readiness</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            The qualifications, accreditations and capabilities CPH needs to move from domestic into commercial / non-domestic work and to bid as Main (Principal) Contractor. Mark what you have, and see your gap at a glance.
          </p>
        </div>
      </div>

      {/* Overall progress */}
      <div className="border-2 border-[#14304F] bg-white p-5" data-testid="readiness-summary">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 font-display text-lg font-bold tracking-tight">
            <ShieldCheck size={18} className="text-[#2F6DA3]" /> Overall readiness
          </div>
          <div className="font-display text-3xl font-extrabold tracking-tight" data-testid="readiness-percent">{percent}%</div>
        </div>
        <div className="h-3 w-full bg-zinc-100 border-2 border-zinc-950">
          <div className="h-full bg-[#2F6DA3] transition-all duration-500" style={{ width: `${percent}%` }} />
        </div>
        <div className="text-xs text-zinc-500 mt-2 font-mono">
          {data?.have ?? 0} of {data?.applicable ?? 0} applicable items in place
          {data?.counts ? ` · ${data.counts.in_progress || 0} in progress · ${data.counts.not_yet || 0} not yet` : ""}
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-20 font-mono text-sm text-zinc-400">Loading…</div>
      ) : (
        <div className="space-y-8">
          {data.groups.map((g) => (
            <div key={g.group} data-testid={`readiness-group-${g.group.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
              <h2 className="font-display text-lg font-bold tracking-tight mb-3 flex items-center gap-2">
                <Building2 size={16} className="text-zinc-400" /> {g.group}
                <span className="text-xs font-mono font-normal text-zinc-400">({g.have}/{g.applicable})</span>
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {g.items.map((it) => {
                  const meta = STATUS_META[it.status] || STATUS_META.not_yet;
                  const Icon = meta.icon;
                  return (
                    <div key={it.id} data-testid={`readiness-item-${it.id}`}
                      className="border-2 border-zinc-200 bg-white p-4 hover:border-[#14304F] transition-colors">
                      <div className="flex items-start gap-2 mb-1">
                        <Icon size={16} className={`${meta.dot} mt-0.5 shrink-0`} />
                        <span className="font-bold text-sm leading-snug">{it.title}</span>
                      </div>
                      <p className="text-xs text-zinc-500 mb-3 ml-6">{it.why}</p>
                      <div className="flex flex-wrap gap-1.5 ml-6">
                        {ORDER.map((s) => (
                          <button
                            key={s}
                            data-testid={`status-${it.id}-${s}`}
                            onClick={() => setStatus(it.id, s)}
                            className={`text-[11px] font-bold uppercase tracking-wide border-2 px-2.5 py-1 transition-colors ${
                              it.status === s ? STATUS_META[s].cls : "bg-white border-zinc-200 text-zinc-500 hover:border-zinc-400"
                            }`}
                          >
                            {STATUS_META[s].label}
                          </button>
                        ))}
                      </div>
                      <input
                        data-testid={`notes-${it.id}`}
                        value={notes[it.id] ?? ""}
                        onChange={(e) => setNotes((p) => ({ ...p, [it.id]: e.target.value }))}
                        onBlur={() => saveNote(it.id)}
                        placeholder="Add a note (e.g. who holds it, renewal date, owner)…"
                        className="ml-6 mt-3 w-[calc(100%-1.5rem)] border-2 border-zinc-200 px-2 py-1.5 text-xs focus:outline-none focus:border-[#2F6DA3]"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
